import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import {
  useGetTenantForSchoolAdminQuery,
  useUpdateTenantSettingsMutation,
  useUploadFaviconMutation,
  useParseAndSaveSamlMetadataMutation,
  useGetContentSafetySettingsQuery,
  useUpdateContentSafetySettingsMutation,
} from "../../graphql/generated/graphql";

import { ArrowCounterClockwise } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { Check } from "@phosphor-icons/react/dist/csr/Check";
import { Palette } from "@phosphor-icons/react/dist/csr/Palette";
import { ChatCircle } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { ShieldCheck } from "@phosphor-icons/react/dist/csr/ShieldCheck";
import NewCustomAxioActionModal from "../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import AdminTabNavigator from "../../components/adminDashboard/AdminTabNavigator/AdminTabNavigator";
import AdminBrandingSettingsContainer from "../../containers/adminDashboard/AdminBrandingSettingsContainer";
import AdminCompanionSettingsContainer from "../../containers/adminDashboard/AdminCompanionSettingsContainer";
import {
  ContentSafetySettings,
  initialModerationSettings,
  ModerationFlag,
} from "../../components/adminDashboard/ContentSafety";
import { useAppConfig } from "../../AppConfigProvider";

function AdminSettingsView() {
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const [activeTab, setActiveTab] = useState("branding");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSamlMetadataModal, setShowSamlMetadataModal] = useState(false);
  const [samlMetadataUrl, setSamlMetadataUrl] = useState("");

  const [buttonLabel, setButtonLabel] = useState("Fetch & Save");
  const [parsedMetadata, setParsedMetadata] = useState<{
    entityId: string;
    ssoUrl: string;
    cert: string;
    expiry: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    logo: "",
    favicon: "",
    primaryColor: "",
    financialInformationUrl: "",
    helpCenterUrl: "",
    schoolResourcesUrl: "",
    officialTranscriptsUrl: "",
  });

  const [originalData, setOriginalData] = useState(formData);

  const [contentSafety, setContentSafety] = useState({
    sexual: true,
    hate: true,
    harassment: true,
    selfHarm: true,
    violence: true,
  });

  const [originalContentSafety, setOriginalContentSafety] = useState(contentSafety);

  const { data: contentSafetyData, refetch: refetchContentSafety } = useGetContentSafetySettingsQuery();

  const [updateContentSafetySettings] = useUpdateContentSafetySettingsMutation();

  const {
    data: tenantData,
    loading: getTenantLoading,
    error: getTenantError,
    refetch: refetchTenant,
  } = useGetTenantForSchoolAdminQuery();

  const [updateTenantSettings] = useUpdateTenantSettingsMutation();
  const [uploadFavicon] = useUploadFaviconMutation();

  const tenant = tenantData?.getTenantForSchoolAdmin?.data?.[0];

  const [parseAndSaveSamlMetadata] = useParseAndSaveSamlMetadataMutation();

  const handleSamlMetadataSubmit = async () => {
    if (!samlMetadataUrl) return setErrorMessage("Please enter a metadata URL");

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { data } = await parseAndSaveSamlMetadata({
        variables: { metadataUrl: samlMetadataUrl },
      });

      const response = data?.parseAndSaveSamlMetadata;

      if (!response?.success) throw new Error(response?.message);
      setParsedMetadata({
        entityId: response.data.issuer,
        ssoUrl: response.data.entryPoint,
        cert: response.data.cert,
        expiry: response.data.expiry || "",
      });
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to import metadata");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoAfterUpload = (logoUrl: string) => {
    updateFormData("logo", logoUrl);
    refetchTenant();
  };

  const handleFaviconUpload = async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg";

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          await handleFaviconFileUpload(file);
        }
      };

      input.click();
    } catch (error) {
      setErrorMessage("Failed to open file picker");
      setIsSubmitting(false);
    }
  };

  const handleFaviconFileUpload = async (file: File) => {
    if (file.size > 1 * 1024 * 1024) {
      setErrorMessage("Favicon file size should not exceed 1MB");
      return;
    }

    const acceptedTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon", "image/svg+xml", "image/jpeg"];
    if (!acceptedTypes.includes(file.type)) {
      setErrorMessage("Only PNG, ICO, SVG, and JPG files are allowed for favicon");
      return;
    }

    // Validate dimensions for raster files; skip SVG and ICO aliases
    if (file.type !== "image/svg+xml" && file.type !== "image/x-icon" && file.type !== "image/vnd.microsoft.icon") {
      const img = new Image();
      img.onload = async () => {
        if (img.width < 16 || img.width > 1024 || img.height < 16 || img.height > 1024) {
          setErrorMessage("Favicon dimensions must be between 16×16 and 1024×1024 pixels");
          return;
        }
        if (img.width !== img.height) {
          setErrorMessage("Favicon must be square (1:1 aspect ratio)");
          return;
        }
        await processFaviconUpload(file);
      };
      img.onerror = () => {
        setErrorMessage("Invalid image file");
      };
      img.src = URL.createObjectURL(file);
    } else if (file.type === "image/x-icon" || file.type === "image/vnd.microsoft.icon") {
      await uploadIcoAsPng(file);
    } else {
      // For SVG files, skip dimension validation
      await processFaviconUpload(file);
    }
  };

  const uploadIcoAsPng = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const base64Data: string = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          try {
            const targetSize = 180;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const side = Math.min(img.width, img.height) || targetSize;
            canvas.width = targetSize;
            canvas.height = targetSize;
            if (!ctx) {
              reject(new Error("Canvas is not supported"));
              URL.revokeObjectURL(objectUrl);
              return;
            }
            const sx = Math.max(0, (img.width - side) / 2);
            const sy = Math.max(0, (img.height - side) / 2);
            ctx.drawImage(img, sx, sy, side, side, 0, 0, targetSize, targetSize);
            const dataUrl = canvas.toDataURL("image/png");
            resolve(dataUrl.split(",")[1]);
          } catch (err) {
            reject(err);
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Invalid ICO image"));
        };
        img.src = objectUrl;
      });

      // approximate byte size of base64 payload
      const estimatedSize = Math.ceil((base64Data.length * 3) / 4);
      const result = await uploadFavicon({
        variables: {
          favicon: base64Data,
          contentType: "image/png",
          size: estimatedSize,
        },
      });

      if (result.data?.uploadFavicon?.success) {
        const faviconUrl = result.data.uploadFavicon.data;
        updateFormData("favicon", faviconUrl);
        refetchTenant();
      } else {
        throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
      }
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to upload favicon");
    } finally {
      setIsSubmitting(false);
    }
  };

  const processFaviconUpload = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(",")[1];

        const result = await uploadFavicon({
          variables: {
            favicon: base64Data,
            contentType: file.type,
            size: file.size,
          },
        });

        if (result.data?.uploadFavicon?.success) {
          const faviconUrl180 = result.data.uploadFavicon.data;
          updateFormData("favicon", faviconUrl180);
          refetchTenant();
        } else {
          throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
        }
        setIsSubmitting(false);
      };
      reader.onerror = () => {
        setErrorMessage("Failed to read file");
        setIsSubmitting(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setErrorMessage(error.message || "Failed to upload favicon");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const settings = contentSafetyData?.getContentSafetySettings?.data?.[0];
    if (settings) {
      const cleaned = {
        sexual: settings.sexual,
        hate: settings.hate,
        harassment: settings.harassment,
        selfHarm: settings.selfHarm,
        violence: settings.violence,
      };

      setContentSafety(cleaned);
      setOriginalContentSafety(cleaned);
    } else {
      console.log(" No settings returned from backend");
    }
  }, [contentSafetyData]);

  // Initialize form data when tenant data loads
  useEffect(() => {
    if (tenant) {
      const initialData = {
        logo: tenant.logo || "",
        favicon: tenant.favicon180Url || tenant.favicon16Url || "",
        primaryColor: tenant.primaryColor || "#434756",
        financialInformationUrl: tenant.financialInformationUrl || "",
        helpCenterUrl: tenant.helpCenterUrl || "",
        schoolResourcesUrl: tenant.schoolResourcesUrl || "",
        officialTranscriptsUrl: tenant.officialTranscriptsUrl || "",
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [tenant]);

  // Check for changes
  useEffect(() => {
    const hasFormChanges =
      JSON.stringify(formData) !== JSON.stringify(originalData) ||
      JSON.stringify(contentSafety) !== JSON.stringify(originalContentSafety);

    setHasChanges(hasFormChanges);
  }, [formData, originalData, contentSafety, originalContentSafety]);

  const moderationFlags: ModerationFlag[] = initialModerationSettings.map((flag) => ({
    ...flag,
    enabled: contentSafety[flag.id as keyof typeof contentSafety] ?? false,
  }));

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrorMessage("");
  };

  const validateHexColor = (color: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const handleColorChange = (color: string) => {
    if (!color.startsWith("#")) {
      color = "#" + color;
    }
    updateFormData("primaryColor", color);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      // Validate hex color
      if (!validateHexColor(formData.primaryColor)) {
        throw new Error("Please enter a valid hex color code (e.g., #434756)");
      }

      const result = await updateTenantSettings({
        variables: {
          settings: {
            primaryColor: formData.primaryColor,
            financialInformationUrl: formData.financialInformationUrl,
            helpCenterUrl: formData.helpCenterUrl,
            schoolResourcesUrl: formData.schoolResourcesUrl,
            officialTranscriptsUrl: formData.officialTranscriptsUrl,
            logo: formData.logo,
          },
        },
      });
      const safetyResult = await updateContentSafetySettings({
        variables: {
          settings: contentSafety,
        },
      });

      if (!safetyResult.data?.updateContentSafetySettings?.success) {
        throw new Error(
          safetyResult.data?.updateContentSafetySettings?.message || "Failed to save content safety settings"
        );
      }

      await refetchContentSafety();

      if (result.data?.updateTenantSettings?.success) {
        setOriginalData(formData);
        setHasChanges(false);
        refetchTenant();
      } else {
        throw new Error(result.data?.updateTenantSettings?.message || "Failed to save settings");
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to save settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setContentSafety(originalContentSafety);
    setHasChanges(false);
    setErrorMessage("");
  };

  const getFaviconPreviewUrl = (): string => {
    const candidate =
      tenant?.favicon180Url ||
      tenant?.favicon16Url ||
      (formData.favicon ? `${formData.favicon}/favicon-180x180.png?alt=media` : "");
    if (!candidate) return "";
    const sep = candidate.includes("?") ? "&" : "?";
    return `${candidate}${sep}t=${Date.now()}`;
  };

  const tabData = [
    { key: "branding", title: "Branding", icon: <Palette size={20} weight="regular" /> },
    ...(isCompanionMode
      ? [
          { key: "aiCompanion", title: "AI Companion", icon: <ChatCircle size={20} weight="regular" /> },
          { key: "contentSafety", title: "Content Safety", icon: <ShieldCheck size={20} weight="regular" /> },
        ]
      : []),
  ];

  if (getTenantLoading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (getTenantError) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Failed to load settings" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}>
            <View>
              <CustomText
                text="Settings"
                size="m"
                useTranslationText={false}
                textType="display"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />

              <CustomSpacing type="vertical" size="s" />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <CustomText
                  text="Configure your institution's branding, authentication, and AI companion settings."
                  size="m"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
                <CustomButton
                  text="Upload SAML Metadata URL"
                  size="s"
                  styleType="primary"
                  onPress={() => setShowSamlMetadataModal(true)}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            <AdminTabNavigator tabData={tabData} activeTab={activeTab} onTabPress={setActiveTab}>
              {(currentTab) => {
                if (currentTab === "branding") {
                  return (
                    <AdminBrandingSettingsContainer
                      formData={formData}
                      updateFormData={updateFormData}
                      handleLogoAfterUpload={handleLogoAfterUpload}
                      handleFaviconUpload={handleFaviconUpload}
                      handleColorChange={handleColorChange}
                      validateHexColor={validateHexColor}
                      getFaviconPreviewUrl={getFaviconPreviewUrl}
                      isSubmitting={isSubmitting}
                      tenantPrimaryColor={tenant?.primaryColor}
                      tenantFavicon180Url={tenant?.favicon180Url}
                    />
                  );
                } else if (currentTab === "aiCompanion") {
                  return <AdminCompanionSettingsContainer />;
                } else if (currentTab === "contentSafety") {
                  return (
                    <ContentSafetySettings
                      moderationSettings={moderationFlags}
                      onSettingsChange={(newFlags) => {
                        const mapped = {
                          sexual: newFlags.find((f) => f.id === "sexual")?.enabled ?? false,
                          hate: newFlags.find((f) => f.id === "hate")?.enabled ?? false,
                          harassment: newFlags.find((f) => f.id === "harassment")?.enabled ?? false,
                          selfHarm: newFlags.find((f) => f.id === "selfHarm")?.enabled ?? false,
                          violence: newFlags.find((f) => f.id === "violence")?.enabled ?? false,
                        };
                        setContentSafety(mapped);
                      }}
                    />
                  );
                }
                return null;
              }}
            </AdminTabNavigator>

            {errorMessage && (
              <View style={styles.errorMessageContainer}>
                <CustomText text={errorMessage} size="s" useTranslationText={false} style={{ color: "#EF4444" }} />
              </View>
            )}

            <CustomSpacing type="vertical" size="xl" />

            <View style={styles.footerActions}>
              <View style={styles.lastSaved}>
                <CustomText
                  text="Last saved: Today at 2:30 PM"
                  size="s"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
              </View>

              <View style={styles.actionButtons}>
                <CustomButton
                  text="Reset Changes"
                  styleType="primaryLight"
                  size="m"
                  bold={true}
                  onPress={handleReset}
                  disabled={!hasChanges || isSubmitting}
                  useTranslationText={false}
                  style={(!hasChanges || isSubmitting) && styles.disabledButton}
                  textStyle={{ color: "#3E68FE" }}
                  leftIcon={<ArrowCounterClockwise size={24} color="#3E68FE" />}
                />
                <CustomButton
                  text={isSubmitting ? "Saving..." : "Save Settings"}
                  styleType="primary"
                  size="m"
                  bold={true}
                  onPress={handleSave}
                  disabled={!hasChanges || isSubmitting}
                  useTranslationText={false}
                  style={(!hasChanges || isSubmitting) && styles.disabledButton}
                  leftIcon={<Check size={24} color="#FFFFFF" />}
                />
              </View>
            </View>
          </MotiView>
        </View>
      </View>
      <NewCustomAxioActionModal
        visible={showSamlMetadataModal}
        onClose={() => {
          setShowSamlMetadataModal(false);
          setSamlMetadataUrl("");
          setParsedMetadata(null);
          setErrorMessage("");
          setButtonLabel("Save");
        }}
        onAction={async () => {
          await handleSamlMetadataSubmit();

          if (parsedMetadata) {
            setButtonLabel("Saved");
            setTimeout(() => setButtonLabel("Fetch & Save"), 3000);
          }
        }}
        modalTexts={{
          title: "Import SAML Metadata",
          ariaLabel: "saml-metadata-upload",
          warningText: "Paste your Identity Provider’s metadata URL below.",
          actionButtonText: parsedMetadata ? "Saved" : "Fetch & Save",
        }}>
        <CustomTextInput
          value={samlMetadataUrl}
          onChangeText={setSamlMetadataUrl}
          placeholder="https://idp.example.com/metadata.xml"
          style={{ marginBottom: 24 }}
        />
        {parsedMetadata && (
          <View style={{ marginTop: 16 }}>
            <CustomText useTranslationText={false} text="Metadata Parsed" weight="bold" style={{ marginBottom: 12 }} />

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="Entity ID: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.entityId} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="SSO URL: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.ssoUrl} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText useTranslationText={false} text="Cert Expires: " size="s" weight="medium" />

              <CustomText
                useTranslationText={false}
                text={parsedMetadata.expiry}
                size="xs"
                style={{
                  color: "#6B7280",
                  marginTop: 2,
                }}
              />
            </View>

            <View style={{ marginBottom: 12 }}>
              <CustomText useTranslationText={false} text="Certificate: " size="s" weight="medium" />
              <CustomText
                useTranslationText={false}
                text={parsedMetadata.cert}
                size="xs"
                style={{ color: "#6B7280", marginTop: 2 }}
              />
            </View>
          </View>
        )}

        {errorMessage && (
          <CustomText text={errorMessage} size="s" style={{ color: "red", marginTop: 12, marginBottom: 12 }} />
        )}
      </NewCustomAxioActionModal>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
    maxWidth: 1280,
    width: "100%",
    alignSelf: "center",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorMessageContainer: {
    backgroundColor: "#FEF2F2",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FECACA",
    marginTop: 16,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  lastSaved: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 12,
  },
  disabledButton: {
    opacity: 0.5,
  },
});

export default AdminSettingsView;
